// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Controller, type Control, type Path } from 'react-hook-form';
import { Button, Card, CardContent, Checkbox } from '@mui/material';
import { AppIcon, Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useTestResultsAndSignature } from './use-test-results-and-signature';
import { ScreenLayout } from '@shared/advisory/account-workflow/layout';
import { ScreenHeader } from '@shared/advisory/account-workflow/layout';
import { NavigationControls } from '@shared/advisory/account-workflow/layout';
import { AppropriatenessTestResult } from '@shared/advisory/account-workflow/components/profiling/appropriateness-result';
import { SuitabilityTestResult } from '@shared/advisory/account-workflow/components/profiling/suitability-result';
import { QuestionLayout } from '@shared/advisory/account-workflow/components/shared/question-layout';
import { ProfileExpirationAlert } from '@shared/advisory/account-workflow/components/profiling/profile-expiration';
import { ServiceCommissionsCard } from '@shared/advisory/account-workflow/components/profiling/service-commissions';
import { ImportantNoticeAlert } from '@shared/advisory/account-workflow/components/profiling/important-notice';
import { PrecontractualInfoCard } from '@shared/advisory/account-workflow/components/profiling/precontractual-info';
import { IncentivesPolicyCard } from '@shared/advisory/account-workflow/components/profiling/incentives-policy-card';
import { ContractDocumentsCard } from '@shared/advisory/account-workflow/components/profiling/contract-documents-card';
import type { ResultAndSignatureFormData } from '@shared/advisory/account-workflow/types';

interface DeclarationCheckboxSlotProps {
  control: Control<ResultAndSignatureFormData>;
  name: Path<ResultAndSignatureFormData>;
  label: React.ReactNode;
  testId: string;
}

export const TestResultsAndSignature = defineComponent({
  displayName: 'TestResultsAndSignature',
  useSetup: useTestResultsAndSignature,
  Slots: {
    DeclarationCheckbox: (props: DeclarationCheckboxSlotProps) => (
      <Card
        variant="outlined"
        className="!rounded-lg !p-7"
        data-testid={`${props.testId}-card`}
      >
        <CardContent className="!p-0">
          <Controller
            control={props.control}
            name={props.name}
            render={({ field }) => (
              <div
                className="flex items-start space-x-2"
                data-testid={props.testId}
              >
                <Checkbox
                  id={`${props.testId}-checkbox`}
                  data-testid={`${props.testId}-checkbox`}
                  checked={field.value || false}
                  onChange={e => field.onChange(e.target.checked)}
                  className="!mt-1 !p-0"
                  required
                />
                <Label
                  htmlFor={`${props.testId}-checkbox`}
                  className="flex flex-1 cursor-pointer !leading-5"
                  data-testid={`${props.testId}-label`}
                >
                  {props.label}
                </Label>
              </div>
            )}
          />
        </CardContent>
      </Card>
    ),
  },
  View: ({
    form,
    handleSign,
    handleRetakeTest,
    canRetakeTest,
    isLoading,
    isAwaitingContractSignature,
    isFormValid,
    convenienceScore,
    riskProfile,
    convenientProductFamilies,
    profileExpirationDate,
    advisoryType,
    advisoryTypeKey,
    hasRetrocessions,
    commissionManagedAssets,
    allFormData,
    hasPassedConvenienceTest,
    Slots,
  }) => {
    return (
      <ScreenLayout
        actions={
          <NavigationControls
            form="result-and-signature-form"
            isFormValid={() => isFormValid() && hasPassedConvenienceTest}
            loading={isLoading}
            nextIcon={<AppIcon icon="PenTool" size="lg" />}
            nextLabel={
              <Translation
                i18nKey="onboarding-process-overview.result-and-signature.sign-button"
                ns="components"
              />
            }
            hidePrevious
          />
        }
      >
        <div className="w-full">
          <ScreenHeader
            icon={<AppIcon icon="CheckCircle" color="success" size={56} />}
            iconPlacement="top"
            title={
              <Translation
                i18nKey="onboarding-process-overview.result-and-signature.title"
                ns="components"
              />
            }
            subtitle={
              <Translation
                i18nKey="onboarding-process-overview.result-and-signature.description"
                ns="components"
              />
            }
            className="mb-6 items-center text-center"
          />

          <div className="mb-6">
            <AppropriatenessTestResult
              productFamilies={convenientProductFamilies}
              score={convenienceScore}
              advisoryType={advisoryType}
            />
          </div>

          <div className="mb-6">
            <SuitabilityTestResult riskProfile={riskProfile} />
          </div>

          <QuestionLayout
            investmentObjectives={allFormData.investmentObjectives}
            financialKnowledge={allFormData.financialKnowledge}
            investmentExperience={allFormData.investmentExperience}
            financialSituation={allFormData.financialSituation}
            sustainability={allFormData.sustainability}
          />

          <ProfileExpirationAlert profileExpirationDate={profileExpirationDate} />

          <div className="mb-6">
            {canRetakeTest && (
              <Button
                color="secondary"
                onClick={handleRetakeTest}
                startIcon={<AppIcon icon="Refresh" size="lg" />}
                className="!w-full"
                data-testid="retake-test-button"
              >
                <Translation
                  i18nKey="onboarding-process-overview.result-and-signature.retake-test-button"
                  ns="components"
                />
              </Button>
            )}
          </div>

          {hasPassedConvenienceTest && !isAwaitingContractSignature && (
            <form
              id="result-and-signature-form"
              onSubmit={form.handleSubmit(handleSign)}
              data-testid="result-and-signature-form"
              className="flex flex-col gap-6"
            >
              <hr className="border-gray-300" />

              <ServiceCommissionsCard
                advisoryTypeKey={advisoryTypeKey}
                commissionManagedAssets={commissionManagedAssets}
                hasRetrocessions={hasRetrocessions}
              />

              <ImportantNoticeAlert />

              <PrecontractualInfoCard advisoryTypeKey={advisoryTypeKey} />

              <IncentivesPolicyCard />

              <ContractDocumentsCard advisoryTypeKey={advisoryTypeKey} />

              <Slots.DeclarationCheckbox
                control={form.control}
                name="precontractualInformationDeclaration"
                label={
                  <Translation
                    i18nKey="onboarding-process-overview.result-and-signature.precontractual-information-declaration"
                    ns="components"
                  />
                }
                testId="precontractual-information-declaration"
              />

              <Slots.DeclarationCheckbox
                control={form.control}
                name="testInformationDeclaration"
                label={
                  <Translation
                    i18nKey="onboarding-process-overview.result-and-signature.test-information-declaration"
                    ns="components"
                  />
                }
                testId="test-information-declaration"
              />
            </form>
          )}
        </div>
      </ScreenLayout>
    );
  },
});
